/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import loadable from '@loadable/component'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Adyen css
import './vendors/adyen/style/adyen.css'

// Components
import {Skeleton} from '@chakra-ui/react'
import {configureRoutes} from './utils/routes-utils'
import routingObject from './utils/static-routes'
import GuestOrder from './pages/guestOrder'
import GuestRegisteredProduct from './pages/guestRegisteredProduct'

const {
    FAQ_PATH,
    LIVE_PATH,
    CONTACT_US_PATH,
    REFERRAL_DASHBOARD_PATH,
    AUTHORIZED_SERVICE_CENTER_PATH,
    PRODUCT_REGISTRATION_PATH,
    HELP_ME_CHOOSE_PATH,
    MANUALS_PATH,
} = routingObject || ''

const fallback = <Skeleton height="75vh" width="100%" />

// Create your pages here and add them to the routes array
// Use loadable to split code into smaller js chunks
const Home = loadable(() => import('./pages/home'), {fallback})
const GenericHtmlPage = loadable(() => import('./pages/generic-html-page'), {fallback})
const ProductDetail = loadable(() => import('./pages/product-detail'), {fallback})
const ProductList = loadable(() => import('./pages/product-list'), {fallback})
const RecipiesPage = loadable(() => import('./pages/recipes-page'), {fallback})
const Login = loadable(() => import('./pages/login'), {fallback})

const ResetPassword = loadable(() => import('./pages/reset-password'), {fallback})

const CompleteRegistration = loadable(() => import('./pages/complete-registration'), {fallback})

const AppLogin = loadable(() => import('./pages/app-login'), {
    fallback,
})
//TODO: remove this import, is for testing purposes only
const TestCPIAuth = loadable(() => import('./pages/testcpiauth'), {
    fallback,
})

//TODO: remove this import, is for testing purposes only
const TestOMSReturnOrder = loadable(() => import('./pages/test-oms-return-order'), {
    fallback,
})
//TODO: remove this import, is for testing purposes only
const TestOMSCancelOrder = loadable(() => import('./pages/test-oms-cancel-order'), {
    fallback,
})
//TODO: remove this import, is for testing purposes only
const TestOMSGetOrderDetails = loadable(() => import('./pages/test-oms-get-order-details'), {
    fallback,
})
//TODO: remove this import, is for testing purposes only
const TestOMSRetrieveFiscalDocuments = loadable(
    () => import('./pages/test-oms-retrieve-fiscal-documents'),
    {
        fallback,
    },
)
//TODO: remove this import, is for testing purposes only
const TestOMSRetrieveFiscalDocumentDetails = loadable(
    () => import('./pages/test-oms-retrieve-fiscal-document-details'),
    {
        fallback,
    },
)
//TODO: remove this import, is for testing purposes only
const TestGetRepairTicketSAPCPI = loadable(() => import('./pages/test-get-repair-ticket-sap-cpi'), {
    fallback,
})

const TestContentstack = loadable(() => import('./pages/test-contentstack'), {fallback})

const Test = loadable(() => import('./pages/test'), {
    fallback,
})

const Registration = loadable(() => import('./pages/registration'), {
    fallback,
})

const Unsubscribe = loadable(() => import('./pages/unsubscribe'), {
    fallback,
})

const RegistrationConfirmation = loadable(() => import('./pages/registration/confirmation'), {
    fallback,
})
const SubscriptionConfirmation = loadable(() => import('./pages/subscription/confirmation'), {
    fallback,
})

const Checkout = loadable(() => import('./pages/checkout'), {
    fallback,
})
const CheckoutConfirmation = loadable(() => import('./pages/checkout/confirmation'), {
    fallback: fallback,
})
const ServiceCenters = loadable(() => import('./pages/authorized-service-center/index'), {
    fallback: fallback,
})
// Checkout Redirect page from Adyen
const AdyenCheckoutRedirect = loadable(() => import('./pages/checkout/redirect'), {
    fallback: fallback,
})

// Checkout Error page from Adyen
const AdyenCheckoutError = loadable(() => import('./pages/checkout/error'), {
    fallback: fallback,
})
const Account = loadable(() => import('./pages/account'), {
    fallback: fallback,
})
//TODO: remove this import, is for testing purposes only
const EmployeeBalance = loadable(() => import('./pages/account/oms-employee-balance'), {
    fallback: fallback,
})
const Library = loadable(() => import('../../library'), {fallback})
const EditorialPage = loadable(() => import('./pages/editorial'), {fallback})
const Cart = loadable(() => import('./pages/cart'), {fallback})
const CartRestore = loadable(() => import('./pages/cart-restore'), {fallback})
const Manuals = loadable(() => import('./pages/manuals'), {fallback})
const Paperless = loadable(() => import('./pages/paperless'), {fallback})
const HelpMeChoose = loadable(() => import('./pages/help-me-choose'), {fallback})
const ErrorAndMaintenancePage = loadable(() => import('./pages/maintenance-page'), {fallback})
// const PageNotFound = loadable(() => import('./pages/page-not-found'), {fallback})
const ProductRegistration = loadable(() => import('./pages/product-registration'), {fallback})
const ProductSearch = loadable(() => import('./pages/product-registration/product-search'), {
    fallback,
})
const ProductSelect = loadable(
    () => import('./pages/product-registration/product-registration-select'),
    {
        fallback,
    },
)
const ProductRegistrationLogin = loadable(
    () => import('./pages/product-registration/product-registration-login'),
    {
        fallback,
    },
)
const ProductRegistrationAccount = loadable(
    () => import('./pages/product-registration/product-registration-account'),
    {
        fallback,
    },
)
const ProductRegistrationForm = loadable(
    () => import('./pages/product-registration/product-registration-form'),
    {
        fallback,
    },
)
const ProductRegistrationConfirm = loadable(
    () => import('./pages/product-registration/product-registration-confirm'),
    {
        fallback,
    },
)
const Wishlist = loadable(() => import('./pages/account/wishlist'), {fallback})

const WishlistShare = loadable(() => import('./pages/account/wishlist-share'), {fallback})

const Faq = loadable(() => import('./pages/faq/index', {fallback}))

const OnLive = loadable(() => import('./pages/onlive/index', {fallback}))

const ContactUs = loadable(() => import('./pages/contact-us'), {fallback})
const ContactUsConfirmation = loadable(() => import('./pages/contact-us/contact-us-confirmation'), {
    fallback,
})
const ReferralDashboard = loadable(() => import('./pages/referral-dashboard'), {
    fallback,
})

const routes = [
    {
        path: '/',
        component: Home,
        exact: true,
    },
    {
        path: '/p/:urlSegment*',
        component: (props) => <ProductDetail key={props.match.params.urlSegment} {...props} />,
    },
    {
        path: '/s/:productId',
        component: Paperless,
    },
    {
        path: '/e/:page*',
        component: EditorialPage,
    },
    {
        path: '/search',
        component: ProductList,
    },
    {
        path: '/c/:urlSegment*',
        component: ProductList,
    },
    {
        path: '/login',
        component: Login,
        exact: true,
    },
    {
        path: '/login/pw',
        component: ResetPassword,
    },
    {
        path: '/registration/complete',
        component: CompleteRegistration,
    },
    {
        path: '/loginJWT',
        component: AppLogin,
        exact: true,
    },
    {
        path: '/registration',
        component: Registration,
        exact: true,
    },
    {
        path: '/registration/confirmation',
        component: RegistrationConfirmation,
        exact: true,
    },
    {
        path: '/subscription/confirmation',
        component: SubscriptionConfirmation,
        exact: true,
    },
    {
        path: '/unsubscribe',
        component: Unsubscribe,
        exact: true,
    },
    {
        path: '/cart/restore',
        component: CartRestore,
        exact: true,
    },
    {
        path: '/cart',
        component: Cart,
        exact: true,
    },
    {
        path: '/checkout',
        component: Checkout,
        exact: true,
    },
    {
        path: '/checkout/redirect',
        component: AdyenCheckoutRedirect,
    },
    {
        path: '/checkout/error',
        component: AdyenCheckoutError,
    },
    {
        path: '/checkout/confirmation/:orderNo',
        component: CheckoutConfirmation,
    },
    {
        path: MANUALS_PATH + '/:rest*',
        component: Manuals,
    },
    {
        path: HELP_ME_CHOOSE_PATH,
        component: HelpMeChoose,
    },
    {
        path: PRODUCT_REGISTRATION_PATH,
        component: ProductRegistration,
        exact: true,
    },
    {
        path: `${PRODUCT_REGISTRATION_PATH}/search`,
        component: ProductSearch,
    },
    {
        path: `${PRODUCT_REGISTRATION_PATH}/select`,
        component: ProductSelect,
    },
    {
        path: `${PRODUCT_REGISTRATION_PATH}/user-detail`,
        component: ProductRegistrationLogin,
    },
    {
        path: `${PRODUCT_REGISTRATION_PATH}/account-detail`,
        component: ProductRegistrationAccount,
    },
    {
        path: `${PRODUCT_REGISTRATION_PATH}/detail`,
        component: ProductRegistrationForm,
    },
    {
        path: `${PRODUCT_REGISTRATION_PATH}/confirmation`,
        component: ProductRegistrationConfirm,
    },
    {
        path: '/orders/:orderId/:orderNo/:originalOrderId',
        component: GuestOrder,
    },
    {
        path: '/registered-product/:guestId/',
        component: GuestRegisteredProduct,
    },
    {
        path: '/account',
        component: Account,
    },
    {
        path: '/wishlist/share',
        component: WishlistShare,
    },
    {
        path: '/wishlist',
        component: Wishlist,
    },
    {
        path: AUTHORIZED_SERVICE_CENTER_PATH,
        component: ServiceCenters,
    },
    {
        path: FAQ_PATH,
        component: Faq,
    },
    {
        path: '/maintenance',
        component: ErrorAndMaintenancePage,
    },
    {
        path: LIVE_PATH,
        component: OnLive,
    },
    {
        path: `${CONTACT_US_PATH}`,
        component: ContactUs,
        exact: true,
    },
    {
        path: `${CONTACT_US_PATH}/confirmation`,
        component: ContactUsConfirmation,
    },
    {
        path: REFERRAL_DASHBOARD_PATH,
        component: ReferralDashboard,
        exact: true,
    },
    {
        path: '/r/recipes',
        component: RecipiesPage,
    },
    {
        path: '/:rest*',
        component: GenericHtmlPage,
    },
]

export default () => {
    const config = getConfig()
    const {customParams} = config

    if (!customParams.isProduction) {
        routes.unshift(
            {
                path: '/components-library',
                component: Library,
            },
            {
                path: '/testcpi',
                component: TestCPIAuth,
            },
            {
                path: '/test',
                component: Test,
            },
            {
                path: '/test2',
                component: Test,
            },
            {
                path: '/test-contentstack/:entryId',
                component: TestContentstack,
            },
            {
                path: '/test-oms-return-order',
                component: TestOMSReturnOrder,
            },
            {
                path: '/employee-balance',
                component: EmployeeBalance,
            },
            {
                path: '/test-oms-cancel-order',
                component: TestOMSCancelOrder,
            },
            {
                path: '/test-oms-get-order-details',
                component: TestOMSGetOrderDetails,
            },
            {
                path: '/test-oms-retrieve-fiscal-documents',
                component: TestOMSRetrieveFiscalDocuments,
            },
            {
                path: '/test-oms-retrieve-fiscal-document-details',
                component: TestOMSRetrieveFiscalDocumentDetails,
            },
            {
                path: '/test-get-repair-ticket-sap-cpi',
                component: TestGetRepairTicketSAPCPI,
            },
        )
    }

    return configureRoutes(routes, config, {
        ignoredRoutes: ['/callback', '*'],
    })
}
